import { styled, css, TextArea, Inline, Button, Spinner } from 'canoe';
import { Field, FieldAttributes, Form, Formik } from 'formik';
import { ENDPOINTS, createCacheKey, useCreatePayload } from 'hammock';
import React, { useState } from 'react';

import { useFirstMate } from '@candu-ai/first-mate/api';
import { useDefaultVariants } from '@candu-ai/styleguide/application-variants';
import { queryClient } from 'commons/fleet/queryClient';

import { ImageStyles, IMAGE_STYLES, DescriptionsByStyle } from './constants';
import { Loading, uploadImage } from './uploadImage';

const Wrapper = styled.div`
  ${({ theme }) => css`
    flex-direction: column;
    flex: 0.4;
    display: flex;
    justify-content: center;
    margin-top: ${theme.spaces.xs};
    width: 100%;
  `}
`;

const ImagePreview = styled.img`
  border-radius: 4px;
  width: 100%;
`;

const ImagesContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    margin-bottom: ${theme.spaces.normal};
  `}
`;

const ImageWrapper = styled.div`
  ${({ theme }) => css`
    margin-top: ${theme.spaces.xs};
    margin-right: ${theme.spaces.xs};
    position: relative;
    overflow: hidden;
    width: calc(50% - ${theme.spaces.xs});

    &:hover div {
      top: 0;
    }
  `}
`;

const ImageHover = styled.div`
  align-items: stretch;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-around;
  left: 0;
  padding: 10px;
  position: absolute;
  transition: top 1s;
  top: 100%;
  width: 100%;
`;

const Label = styled.p`
  ${({ theme }) => css`
    color: ${theme.colors.OnyxGray};
    margin-top: ${theme.spaces.normal};
    margin-bottom: ${theme.spaces.xs};
  `}
`;

const Badge = styled.div<{ isActive: boolean }>`
  ${({ theme, isActive }) => css`
    background-color: transparent;
    border: 1px solid ${isActive ? theme.colors.BrandPurple : theme.colors.OnyxGrayL10};
    border-radius: 4px;
    color: ${isActive ? theme.colors.BrandPurple : theme.colors.OnyxGray};
    cursor: pointer;
    padding: 2px 12px;

    &:focus {
      outline: none;
    }
  `}
`;

export const GenerateImage = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [aiImages, setAiImages] = useState<string[]>([]);
  const [latestValues, setLatestValues] = useState<any>();
  const firstMate = useFirstMate();
  const { getAll } = useCreatePayload(ENDPOINTS.images);

  const { data: defaultVariants } = useDefaultVariants();
  const currentStyleRules = defaultVariants?.variants?.Global?.base?.selectors?.global?.rules || {};

  const onSaveImage = async (image: string) => {
    const res: Response = await fetch(image);
    const blob: Blob = await res.blob();
    const file = new File([blob], `${latestValues?.style || ''} ${new Date().toString()}.png`, {
      type: 'image/png',
    });

    uploadImage(reloadImages, ({ status }: Loading) => setLoading(status))([file]);
  };

  const reloadImages = ({ src }: any) => {
    if (!src) {
      return;
    }
    queryClient.invalidateQueries(
      createCacheKey(getAll({ query: { finder: 'byOrganizationId' } })),
    );
  };

  const onSubmit = async (values: any) => {
    if (!firstMate) {
      return;
    }
    if (!values.description) {
      return;
    }
    setLatestValues(values);
    const prompt =
      values.endDescription ||
      DescriptionsByStyle[IMAGE_STYLES[values.style]]?.(values.description, currentStyleRules);
    setLoading(true);

    const res = (await firstMate.image({
      prompt,
      n: prompt.length > 1000 || values.aiStyle === 'natural' ? 1 : 4,
      model: prompt.length > 1000 || values.aiStyle === 'natural' ? 'dall-e-3' : 'dall-e-2',
      style: values.aiStyle,
      response_format: 'b64_json',
    })) as any;

    setLoading(false);
    if (res.res.data) {
      setAiImages([
        ...res.res.data.map(
          (image: { b64_json: string }) => `data:image/jpeg;base64,${image.b64_json}`,
        ),
        ...aiImages,
      ]);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  if (aiImages.length) {
    return (
      <Wrapper>
        <ImagesContainer>
          {aiImages.map((url) => (
            <ImageWrapper key={url}>
              <ImagePreview src={url} />
              <ImageHover>
                <Button
                  onClick={() => {
                    const image = new Image();
                    image.src = url;
                    const newWindow = window.open('', '_blank');
                    newWindow?.document.write(image.outerHTML);
                  }}
                >
                  Preview
                </Button>
                <Button onClick={() => onSaveImage(url)}>Save</Button>
              </ImageHover>
            </ImageWrapper>
          ))}
        </ImagesContainer>

        <Button onClick={() => onSubmit(latestValues)}>Try again</Button>
        <Button variant="contrast" marginTop="xs" onClick={() => setAiImages([])}>
          Go Back
        </Button>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Formik
        initialValues={{ description: '', style: 'none', aiStyle: 'vivid' }}
        onSubmit={onSubmit}
      >
        {({ setFieldValue, values }) => (
          <Form>
            <Field required name="description">
              {({ field }: FieldAttributes<any>) => (
                <TextArea
                  {...field}
                  value={field.value || ''}
                  placeholder="Describe what you want to see. For example, “onboarding icon”."
                />
              )}
            </Field>

            <Label>Select Style</Label>
            <Field name="style">
              {({ field }: FieldAttributes<any>) => (
                <Inline space="xs">
                  {ImageStyles.map((style) => (
                    <Badge
                      key={style.value}
                      onClick={() => {
                        setFieldValue('style', style.value);
                        const endDescription = DescriptionsByStyle[IMAGE_STYLES[style.value]]?.(
                          values.description,
                          currentStyleRules,
                        );
                        setFieldValue('endDescription', endDescription);
                      }}
                      isActive={field.value === style.value}
                    >
                      {style.label}
                    </Badge>
                  ))}
                </Inline>
              )}
            </Field>

            <Label>Final description</Label>
            <Field required name="endDescription">
              {({ field }: FieldAttributes<any>) => (
                <TextArea
                  {...field}
                  value={field.value || ''}
                  marginTop="xs"
                  placeholder="Describe what you want to see. For example, “onboarding icon”."
                />
              )}
            </Field>

            <Label>Ai Style</Label>
            <Field required name="aiStyle">
              {({ field }: FieldAttributes<any>) => (
                <Inline space="xs">
                  <Badge
                    onClick={() => setFieldValue('aiStyle', 'vivid')}
                    isActive={field.value === 'vivid'}
                  >
                    Vivid
                  </Badge>
                  <Badge
                    onClick={() => setFieldValue('aiStyle', 'natural')}
                    isActive={field.value === 'natural'}
                  >
                    Natural
                  </Badge>
                </Inline>
              )}
            </Field>

            <Button fullWidth type="submit" marginTop="normal">
              Generate Image
            </Button>
          </Form>
        )}
      </Formik>
    </Wrapper>
  );
};
